/**
 * Ajusta la altura de un recorte de canvas para que el borde caiga en un
 * espacio en blanco entre renglones, nunca a mitad de un glifo.
 *
 * html2canvas y getClientRects pueden diferir unos px; este paso corrige el corte real.
 */

/** Filas mínimas con tinta para considerar que hay contenido real (no ruido AA). */
const MIN_FILAS_TINTA_PAGINA = 3;

function filaTieneTintaEn(ctx, y, width, canvasHeight) {
  if (y < 0 || y >= canvasHeight) return false;
  const { data } = ctx.getImageData(0, y, width, 1);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245) {
      return true;
    }
  }
  return false;
}

/** Última fila (px canvas) con tinta real; -1 si todo es blanco. */
export function encontrarUltimaFilaConTinta(canvas, anchoUtil) {
  if (!canvas) return -1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return -1;

  const width = Math.max(1, Math.min(Math.floor(anchoUtil), canvas.width));
  const step = Math.max(1, Math.floor(canvas.height / 2000));

  for (let y = canvas.height - 1; y >= 0; y -= step) {
    if (filaTieneTintaEn(ctx, y, width, canvas.height)) {
      let fine = y;
      for (let yy = Math.min(canvas.height - 1, y + step); yy >= y; yy -= 1) {
        if (filaTieneTintaEn(ctx, yy, width, canvas.height)) {
          fine = yy;
          break;
        }
      }
      return fine;
    }
  }
  return -1;
}

/** ¿El recorte [srcY, srcY+srcH) tiene tinta suficiente (no solo ruido)? */
export function sliceTieneTinta(canvas, srcY, srcH, anchoUtil) {
  if (!canvas || srcH <= 0) return false;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return true;

  const width = Math.max(1, Math.min(Math.floor(anchoUtil), canvas.width));
  const y0 = Math.max(0, Math.floor(srcY));
  const y1 = Math.min(canvas.height, Math.ceil(srcY + srcH));
  if (y1 <= y0) return false;

  const step = Math.max(1, Math.floor((y1 - y0) / 80));
  let filasConTinta = 0;
  for (let y = y0; y < y1; y += step) {
    if (filaTieneTintaEn(ctx, y, width, canvas.height)) {
      filasConTinta += 1;
      if (filasConTinta >= MIN_FILAS_TINTA_PAGINA) return true;
    }
  }
  return false;
}

/**
 * Quita saltos finales que producirían hojas en blanco (sin tinta).
 */
export function filtrarSaltosPaginasEnBlanco({
  saltos,
  totalCss,
  canvas,
  scale,
  contentWidthCanvas,
  altoContenidoCss,
}) {
  if (!Array.isArray(saltos) || saltos.length <= 1) {
    return saltos?.length ? saltos : [0];
  }

  const resultado = [...saltos];
  while (resultado.length > 1) {
    const indice = resultado.length - 1;
    const inicio = resultado[indice];
    const alto = Math.min(altoContenidoCss, Math.max(0, totalCss - inicio));
    const srcY = Math.floor(inicio * scale);
    const srcH = Math.floor(alto * scale);
    if (sliceTieneTinta(canvas, srcY, srcH, contentWidthCanvas)) {
      break;
    }
    resultado.pop();
  }
  return resultado;
}

export function ajustarAltoSliceSinPartirGlifos(
  canvas,
  srcY,
  srcH,
  anchoUtil,
  scale = 3
) {
  if (!canvas || srcH <= scale * 2) return Math.max(0, srcH);

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return srcH;

  const width = Math.max(1, Math.min(Math.floor(anchoUtil), canvas.width));
  const yEnd = Math.min(Math.floor(srcY + srcH), canvas.height);
  const yStart = Math.max(0, Math.floor(srcY));
  if (yEnd <= yStart) return 0;

  const lookback = Math.min(yEnd - yStart - scale, Math.round(44 * scale));
  const minY = Math.max(yStart + Math.round(10 * scale), yEnd - lookback);
  const whiteRunNeeded = Math.max(2, Math.ceil(scale * 0.75));

  const filaTieneTinta = (y) => filaTieneTintaEn(ctx, y, width, canvas.height);

  const margen = Math.max(1, Math.ceil(scale));
  let cortaTinta = false;
  for (let y = Math.max(yStart, yEnd - margen); y < yEnd; y += 1) {
    if (filaTieneTinta(y)) {
      cortaTinta = true;
      break;
    }
  }

  if (
    !cortaTinta &&
    yEnd > yStart &&
    yEnd < canvas.height &&
    filaTieneTinta(yEnd - 1) &&
    filaTieneTinta(yEnd)
  ) {
    cortaTinta = true;
  }

  if (!cortaTinta) {
    return yEnd - yStart;
  }

  let y = yEnd - 1;
  while (y >= minY) {
    if (!filaTieneTinta(y)) {
      const bottomWhite = y;
      while (y >= minY && !filaTieneTinta(y)) {
        y -= 1;
      }
      const topWhite = y + 1;
      const run = bottomWhite - topWhite + 1;
      if (run >= whiteRunNeeded) {
        const cutY = topWhite + Math.floor(run / 2);
        return Math.max(scale, cutY - yStart);
      }
      continue;
    }
    y -= 1;
  }

  const fallback = Math.round(22 * scale);
  return Math.max(scale, yEnd - yStart - fallback);
}

/**
 * A partir de saltos CSS (DOM), recalcula cada borde con el canvas real.
 * Recorta la altura total a la última tinta para no generar hoja en blanco.
 * Ignora saltos DOM prematuros que dejarían la hoja casi vacía.
 */
export function refinarSaltosConCanvas({
  saltosDom,
  totalCss,
  canvas,
  scale,
  contentWidthCanvas,
  altoContenidoCss,
}) {
  const ultimaTinta = encontrarUltimaFilaConTinta(canvas, contentWidthCanvas);
  // Margen mínimo: un margen grande empuja a una hoja extra entre PCs.
  const margenCss = 2;
  const totalEfectivo =
    ultimaTinta >= 0
      ? Math.min(totalCss, ultimaTinta / scale + margenCss)
      : totalCss;

  const avanceMinimo = altoContenidoCss * 0.7;
  const saltos = [0];
  let cursor = 0;
  let domIdx = 1;

  const maxPaginas = Math.max(
    50,
    Math.ceil(totalEfectivo / Math.max(altoContenidoCss * 0.25, 1)) + 5
  );

  const cortarDesde = (proposedCss) => {
    let srcY = Math.floor(cursor * scale);
    let srcH = Math.floor((proposedCss - cursor) * scale);
    srcH = Math.min(Math.max(0, srcH), Math.max(0, canvas.height - srcY));
    if (srcH <= 0) return cursor;
    srcH = ajustarAltoSliceSinPartirGlifos(
      canvas,
      srcY,
      srcH,
      contentWidthCanvas,
      scale
    );
    return (srcY + srcH) / scale;
  };

  while (cursor < totalEfectivo - 8 && saltos.length < maxPaginas) {
    while (domIdx < saltosDom.length && saltosDom[domIdx] <= cursor + 0.5) {
      domIdx += 1;
    }

    const topePagina = Math.min(cursor + altoContenidoCss, totalEfectivo);
    let proposed = topePagina;

    // Solo respetar salto DOM si la hoja queda suficientemente llena
    if (domIdx < saltosDom.length) {
      const domBreak = saltosDom[domIdx];
      if (domBreak - cursor >= avanceMinimo) {
        proposed = Math.min(proposed, domBreak);
      }
    }

    let next = cortarDesde(proposed);

    // Si el corte quedó demasiado pronto, forzar llenado de hoja
    if (
      next - cursor < avanceMinimo &&
      cursor + altoContenidoCss < totalEfectivo - 8
    ) {
      next = cortarDesde(topePagina);
    }

    if (next <= cursor + 4) {
      next = cortarDesde(topePagina);
      if (next <= cursor + 4) break;
    }

    if (next >= totalEfectivo - 2) break;

    // Nunca empujar un salto que deje una hoja raquítica (salvo fin de documento)
    if (
      next - cursor < avanceMinimo &&
      totalEfectivo - cursor > altoContenidoCss * 0.9
    ) {
      next = Math.min(cursor + altoContenidoCss, totalEfectivo);
      if (next <= cursor + 4) break;
    }

    saltos.push(next);
    cursor = next;
  }

  return filtrarSaltosPaginasEnBlanco({
    saltos,
    totalCss: totalEfectivo,
    canvas,
    scale,
    contentWidthCanvas,
    altoContenidoCss,
  });
}
